import type {
  FormHeaderLogoSize,
  FormLayoutVariant,
  FormStyle,
  FormStyleImage,
  FormStyleImagePosition,
} from "@smartsuite/types";
import { Colors, GrayColors, matchingDeepWashedColorMap, solutionColors } from "@smartsuite/types";
import type { CSSProperties } from "react";

import defaultFormFullBackgroundUrl from "../assets/images/form-default-background.webp";
import defaultFormBannerBackgroundUrl from "../assets/images/form-default-banner-background.webp";

import type { FormState } from "../types/form";

export const DEFAULT_FORM_LAYOUT: FormLayoutVariant = "classic";
export const getSolutionColorStyles = (solutionColor: string | null | undefined): CSSProperties => {
  if (!solutionColor) return {};
  const colorPalette = solutionColors.find(({ primary }) => primary === solutionColor);
  if (!colorPalette) return {};
  return {
    "--solution-primary-color": colorPalette.primary,
    "--solution-secondary-color": colorPalette.secondary,
    "--solution-highlight-color": colorPalette.highlight,
    "--solution-hover-color": colorPalette.hover,
    // Deep-washed (muted, light) variant of the solution color, mirroring the
    // builder's `--solution-dw-color`. Used for surfaces like the form footer
    // that want a soft tint rather than the vivid primary.
    "--solution-deep-washed-color":
      matchingDeepWashedColorMap[colorPalette.primary] ?? Colors.dwGrey,
  } as CSSProperties;
};
export const DEFAULT_FORM_IMAGE_BRIGHTNESS = 100;
// Two bundled defaults so the banner layout doesn't pay the cost of a 2240×1493 hero image
// for a 180px-tall strip. The full background stays full-size for the `full` layout, where
// the image actually fills the viewport.
export const DEFAULT_FORM_FULL_BACKGROUND_URL = defaultFormFullBackgroundUrl;
export const DEFAULT_FORM_BANNER_BACKGROUND_URL = defaultFormBannerBackgroundUrl;

export const DEFAULT_FORM_BACKGROUND_COLOR = "grey";
export const DEFAULT_FORM_BUTTON_COLOR = "solution";

export const DEFAULT_FORM_HEADER_LOGO_SIZE: FormHeaderLogoSize = "l";

const FORM_HEADER_LOGO_SIZE_PX: Record<FormHeaderLogoSize, { width: number; height: number }> = {
  s: { width: 140, height: 50 },
  m: { width: 170, height: 60 },
  l: { width: 200, height: 70 },
  xl: { width: 230, height: 80 },
  xxl: { width: 260, height: 90 },
};

const resolveLogoSizeEntry = (
  size: FormHeaderLogoSize | null | undefined,
): { width: number; height: number } =>
  (size && FORM_HEADER_LOGO_SIZE_PX[size]) ??
  FORM_HEADER_LOGO_SIZE_PX[DEFAULT_FORM_HEADER_LOGO_SIZE];

export const getFormHeaderLogoHeightPx = (size: FormHeaderLogoSize | null | undefined): number =>
  resolveLogoSizeEntry(size).height;

export const getFormHeaderLogoWidthPx = (size: FormHeaderLogoSize | null | undefined): number =>
  resolveLogoSizeEntry(size).width;

const OLD_BASE_BY_NAME: Record<string, string> = {
  blue: Colors.bBlue,
  lightBlue: Colors.bLightBlue,
  green: Colors.bGreen,
  red: Colors.bRed,
  orange: Colors.bOrange,
  yellow: Colors.bYellow,
  purple: Colors.bPurple,
  pink: Colors.bPink,
  teal: Colors.bTeal,
  darkGrey: Colors.bGrey,
};

const ENTERPRISE_BASE_BY_NAME: Record<string, string> = {
  blue: Colors.epbBlue,
  lightBlue: Colors.epbLightBlue,
  green: Colors.epbGreen,
  red: Colors.epbRed,
  orange: Colors.epbOrange,
  yellow: Colors.epbYellow,
  purple: Colors.epbPurple,
  pink: Colors.epbPink,
  teal: Colors.epbTeal,
  darkGrey: Colors.epbGrey,
};

// Primary colors of the enterprise workspace theme. A solution whose color is
// one of these resolves form-style palettes against the enterprise base
// colors; legacy solution colors resolve against the old base palette.
const ENTERPRISE_SOLUTION_PRIMARIES = new Set<string>(
  Object.values(ENTERPRISE_BASE_BY_NAME).map((color) => color.toLowerCase()),
);

export const isEnterpriseSolutionColor = (color: string | null | undefined): boolean => {
  // Unknown/missing color falls back to the enterprise palette — it is the
  // default for new workspaces and matches the Forms 2.0 design spec.
  if (!color) {
    return true;
  }
  return ENTERPRISE_SOLUTION_PRIMARIES.has(color.toLowerCase());
};

export const getFormBackgroundColorCss = (
  value: string | null | undefined,
  options: { useEnterprisePalette?: boolean; layout?: FormLayoutVariant } = {},
): string => {
  // Forms 2.0 styles resolve against the enterprise (muted) palette per the
  // Figma spec, matching the builder's color pickers. Pass
  // `useEnterprisePalette: false` to resolve against the legacy vivid palette.
  const { useEnterprisePalette = true } = options;
  const resolved = value ?? DEFAULT_FORM_BACKGROUND_COLOR;
  if (resolved === "solution") {
    return "var(--solution-primary-color)";
  }
  if (resolved === "grey") {
    return GrayColors["G-1"];
  }
  if (resolved === "white") {
    return GrayColors["G-0"];
  }
  if (resolved === "black") {
    // "Icon-Dark" (#2D2D2D), surfaced to users as Black.
    return Colors.dGrey;
  }
  const map = useEnterprisePalette ? ENTERPRISE_BASE_BY_NAME : OLD_BASE_BY_NAME;
  return map[resolved] ?? GrayColors["G-1"];
};

export const getFormButtonColorCss = (
  value: string | null | undefined,
  options: { useEnterprisePalette?: boolean } = {},
): string => {
  const { useEnterprisePalette = true } = options;
  const resolved = value ?? DEFAULT_FORM_BUTTON_COLOR;
  if (resolved === "solution") {
    return "var(--solution-primary-color)";
  }
  if (resolved === "black") {
    // "Icon-Dark" (#2D2D2D), surfaced to users as Black.
    return Colors.dGrey;
  }
  const map = useEnterprisePalette ? ENTERPRISE_BASE_BY_NAME : OLD_BASE_BY_NAME;
  return map[resolved] ?? "var(--solution-primary-color)";
};

// New forms default to no footer; the builder opts a form in explicitly. When a
// footer is enabled but no color has been chosen it defaults to white, unlike
// the form background which defaults to grey.
export const DEFAULT_FORM_FOOTER_BACKGROUND_COLOR = "white";

export const getFormFooterEnabled = (style?: FormStyle | null): boolean =>
  style?.footer_enabled === true;

/**
 * Resolve the stored form footer background value to the CSS color to apply.
 *
 * This mirrors the builder's `getFormBackgroundAppliedColor`: the footer color
 * picker persists either a keyword (`"solution"` / `"white"`) or the literal
 * deep-washed hex of the chosen swatch (e.g. `#EEF3FC`). Keywords map to their
 * applied color; a stored hex is already the applied (deep-washed) color and is
 * used as-is. Anything unrecognized falls back to white, matching the builder.
 *
 * The `"solution"` keyword resolves to `--solution-deep-washed-color`, matching
 * the builder's `getFormBackgroundAppliedColor`, which applies the deep-washed
 * solution color (`rgb(var(--solution-dw-color))`) as the footer background.
 */
export const getFormFooterBackgroundColorCss = (value: string | null | undefined): string => {
  const resolved = value ?? DEFAULT_FORM_FOOTER_BACKGROUND_COLOR;
  if (resolved === "solution") {
    return "var(--solution-deep-washed-color)";
  }
  if (resolved.startsWith("#")) {
    return resolved;
  }
  return GrayColors["G-0"];
};

export const getFormAccentColorCss = (
  formState: FormState | null | undefined,
  solutionColor: string | null | undefined,
  options: { appearanceStyleEnabled?: boolean; useEnterprisePalette?: boolean } = {},
): string => {
  const buttonColor = options.appearanceStyleEnabled ? formState?.style?.button_color : undefined;
  const buttonCss = getFormButtonColorCss(buttonColor, {
    useEnterprisePalette: options.useEnterprisePalette,
  });
  if (buttonCss !== "var(--solution-primary-color)") {
    return buttonCss;
  }
  const solutionStyles = getSolutionColorStyles(solutionColor) as Record<string, string>;
  return solutionStyles["--solution-primary-color"] ?? buttonCss;
};

export const DEFAULT_FORM_BANNER_POSITION: FormStyleImagePosition = {
  x: 0.5,
  y: 0.5,
};

export const DEFAULT_FORM_FULL_POSITION: FormStyleImagePosition = {
  x: 0.5,
  y: 0,
};

const clampPercentage = (value: number): number => Math.max(0, Math.min(100, value));

export const getFormLayout = (style?: FormStyle | null): FormLayoutVariant =>
  style?.layout ?? DEFAULT_FORM_LAYOUT;

/**
 * Whether the form header (logo strip) should render.
 *
 * Mirrors the builder's `getEffectiveHeaderEnabled`: `header_enabled` is only
 * persisted once someone toggles the header switch, so forms styled before that
 * toggle existed carry `header_enabled: null` alongside a populated
 * `logo_handle`. The builder falls back to logo presence for those, and preview
 * and the shared link must resolve the header the same way — otherwise a
 * configured logo shows in the builder and silently disappears in the rendered
 * form.
 */
export const getEffectiveHeaderEnabled = (formState?: Partial<FormState> | null): boolean => {
  const explicit = formState?.style?.header_enabled;
  if (typeof explicit === "boolean") {
    return explicit;
  }
  return Boolean(formState?.logo_handle);
};

export const getBannerImageStyle = (formState?: Partial<FormState> | null): FormStyleImage => ({
  ...(formState?.style?.banner_image ?? {
    handle: null,
    brightness: DEFAULT_FORM_IMAGE_BRIGHTNESS,
  }),
  position: formState?.style?.banner_image?.position ?? DEFAULT_FORM_BANNER_POSITION,
});

export const getFullImageStyle = (formState?: Partial<FormState> | null): FormStyleImage => ({
  ...(formState?.style?.full_image ?? {
    handle: null,
    brightness: DEFAULT_FORM_IMAGE_BRIGHTNESS,
  }),
  position: formState?.style?.full_image?.position ?? DEFAULT_FORM_FULL_POSITION,
});

export const getBackgroundPosition = (position?: FormStyleImagePosition | null): string => {
  const nextPosition = position ?? DEFAULT_FORM_BANNER_POSITION;
  return `${nextPosition.x * 100}% ${nextPosition.y * 100}%`;
};

export const getBrightnessValue = (imageStyle?: FormStyleImage | null): number =>
  clampPercentage(imageStyle?.brightness ?? DEFAULT_FORM_IMAGE_BRIGHTNESS);

// Forms are a public-facing renderer; even though Filestack-derived URLs are trusted today,
// any future code path that pipes raw server input through here must not be able to break
// out of the CSS url() context. The bundled default asset is trusted by reference; everything
// else must be http(s), and we escape characters that would terminate the quoted string.
const sanitizeBackgroundUrl = (imageUrl: string): string | null => {
  if (
    imageUrl === DEFAULT_FORM_FULL_BACKGROUND_URL ||
    imageUrl === DEFAULT_FORM_BANNER_BACKGROUND_URL
  ) {
    return imageUrl;
  }
  if (!/^https?:\/\//i.test(imageUrl)) return null;
  return imageUrl.replace(/[\\"]/g, (char) => `\\${char}`);
};

export const getBackgroundImageCss = (imageUrl: string, brightness: number): string => {
  const safeUrl = sanitizeBackgroundUrl(imageUrl);
  if (!safeUrl) return "none";

  const alpha = (100 - clampPercentage(brightness)) / 100;

  if (alpha <= 0) {
    return `url("${safeUrl}")`;
  }

  return `linear-gradient(rgba(0, 0, 0, ${alpha}), rgba(0, 0, 0, ${alpha})), url("${safeUrl}")`;
};
